import React, { useMemo } from "react";
import { Image, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import "./EnableNotificationsDialog.scss";
import BottomDrawer from "../../BottomDrawer/BottomDrawer";
import {
  FunctionWithVoidReturn,
  GenericModalProps,
} from "../../../utils/constants/generic-types";
import PrimaryActionButton from "../../Buttons/PrimaryActionButton/PrimaryActionButton";
import ImagePushNotifications from "../../../images/push-notifications-images.png";
import { EnableNotificationsSteps } from "../../../hooks/useNotifications";
import { createAppIntent } from "../../../utils/helpers";
import useGetApp from "../../../hooks/useGetApp";

interface EnableNotificationsDialogProps extends GenericModalProps {
  handleNotifyMe: () => Promise<void>;
  handleGetApp?: FunctionWithVoidReturn;
  enableNotificationsStep: EnableNotificationsSteps;
}

function useEnableNotificationsDialogCopies() {
  const { ctaText } = useGetApp();
  const { t } = useTranslation("common");

  const mainCopies = useMemo(
    () => ({
      [EnableNotificationsSteps.TURN_ON_NOTIFICATIONS]: t(
        "notifications.dont_miss_out"
      ),
      [EnableNotificationsSteps.OPEN_CHROME_BROWSER]: t(
        "notifications.open_chrome_browser_to_continue"
      ),
    }),
    [t]
  );

  const subCopies = useMemo(
    () => ({
      [EnableNotificationsSteps.TURN_ON_NOTIFICATIONS]: t(
        "notifications.be_the_first_to_know_about_rewards_and_tournaments"
      ),
      [EnableNotificationsSteps.OPEN_CHROME_BROWSER]: t(
        "notifications.you_can_enable_notifications_only_on_chrome"
      ),
    }),
    [t]
  );

  const primaryCtas = useMemo(
    () => ({
      [EnableNotificationsSteps.TURN_ON_NOTIFICATIONS]: t(
        "notifications.turn_on_notifications"
      ),
      [EnableNotificationsSteps.OPEN_CHROME_BROWSER]: t(
        "notifications.open_chrome_browser"
      ),
    }),
    [t]
  );

  const secondaryCtas = useMemo(
    () => ({
      [EnableNotificationsSteps.TURN_ON_NOTIFICATIONS]: t(
        "download_app_prompt.remind_me_later"
      ),
      [EnableNotificationsSteps.OPEN_CHROME_BROWSER]: t("use_or_get_app", {
        action: ctaText,
      }),
    }),
    [t, ctaText]
  );

  return { mainCopies, subCopies, primaryCtas, secondaryCtas };
}

export default function EnableNotificationsDialog({
  isOpen,
  onClose,
  handleNotifyMe,
  enableNotificationsStep,
}: EnableNotificationsDialogProps) {
  const { mainCopies, subCopies, primaryCtas } =
    useEnableNotificationsDialogCopies();

  const handlePrimaryAction = () => {
    if (
      enableNotificationsStep === EnableNotificationsSteps.TURN_ON_NOTIFICATIONS
    ) {
      handleNotifyMe();
      return;
    }
    const { hostname: baseUrl, pathname: path } = window.location;
    window.location.href = createAppIntent(baseUrl, path);
  };

  return (
    <BottomDrawer isOpen={isOpen} onClose={onClose}>
      <Stack className="py-3 EnableNotificationsDialog">
        <Image
          src={ImagePushNotifications}
          width={290}
          height={119}
          alt="Push notification screenshots"
        />
        <Stack gap={4}>
          <Stack gap={2} className="text-center Copy">
            <h2 className="SpecialFontSettings Heading">
              {mainCopies[enableNotificationsStep]}
            </h2>
            <p className="SpecialFontSettings SubText">
              {subCopies[enableNotificationsStep]}
            </p>
          </Stack>
          <Stack className="Ctas">
            <PrimaryActionButton
              size="lg"
              text={primaryCtas[enableNotificationsStep]}
              onClick={handlePrimaryAction}
            />
          </Stack>
        </Stack>
      </Stack>
    </BottomDrawer>
  );
}
